import React, { useState } from "react";

import { ChangeEventTimelineEvent } from "models";

import { Icon, Typography } from "components";

import { ChangeEventCauseEffect } from "pages/admin/verifications/components/changeEvents/ChangeEventCauseEffect";
import { ChangeEventDiffTable } from "pages/admin/verifications/components/changeEvents/ChangeEventDiffTable";
import { ChangeEventRawJson } from "pages/admin/verifications/components/changeEvents/ChangeEventRawJson";
import { Highlight } from "pages/admin/verifications/components/changeEvents/Highlight";
import {
  TOP_KV,
  actionClass,
  actionIcon,
  actionLabel,
  deltaDirection,
  describeEvent,
  factIcon,
  fmtDate,
  fmtDelta,
  fmtNum,
  fmtTime,
  shortId,
  stateClassFor,
  summarize,
} from "pages/admin/verifications/utils/changeEventTimeline";

type ChangeEventCardProps = {
  event: ChangeEventTimelineEvent;
  query?: string;
  onJump: (eventId: string) => void;
};

/** Before → after in one place, so the ledger reads correctly in either sort order. */
const Amounts = ({ event }: { event: ChangeEventTimelineEvent }) => {
  const { balanceAfter, balanceBefore, delta, type } = event;
  if (!balanceAfter) return null;

  // the API calls the points.calculated running total `settled`; the page calls it what it is
  const isVerification = type === "points.calculated";
  const label = isVerification ? "verification" : "pending";
  const balance = isVerification ? balanceAfter.settled : balanceAfter.pending;

  return (
    <div className="ce-amounts">
      <span className={`ce-delta ${deltaDirection(delta)}`}>{fmtDelta(delta)}</span>
      <span className="ce-hint">{`${label} ${fmtNum(balanceBefore)} → ${fmtNum(balance)}`}</span>
    </div>
  );
};

/** Name and actorType are two different facts: "Anna Weber, but the row arrived as admin" needs both. */
const Actor = ({ event, query }: { event: ChangeEventTimelineEvent; query?: string }) => {
  const { actorId, actorName, actorRole, actorType, reason } = event;

  return (
    <div className="ce-actor">
      <Icon iconName="tabler/user" size={14} />
      {actorName && (
        <span className="ce-who">
          <Highlight text={actorName} query={query} />
        </span>
      )}
      <span className="ce-atype">
        <Highlight text={actorType} query={query} />
      </span>
      {actorRole && (
        <span className="ce-arole">
          <Highlight text={actorRole} query={query} />
        </span>
      )}
      {!actorName && actorId && <span className="ce-anon ce-mono">{shortId(actorId)}</span>}
      {reason && (
        <span className="ce-reason">
          <Highlight text={reason} query={query} />
        </span>
      )}
    </div>
  );
};

const Fact = ({ label, value, query }: { label: string; value: string; query?: string }) => {
  const stateClass = stateClassFor(label, value);

  return (
    <div className="ce-fact">
      <span className="ce-fact-icon">
        <Icon iconName={factIcon(label)} size={14} />
      </span>
      <span className="ce-fact-text">
        <span className="ce-fact-label">
          <Highlight text={label} query={query} />
        </span>
        <b className={stateClass ? `ce-state ${stateClass}` : undefined}>
          <Highlight text={value} query={query} />
        </b>
      </span>
    </div>
  );
};

export const ChangeEventCard = ({ event, query, onJump }: ChangeEventCardProps) => {
  const [open, setOpen] = useState(false);

  const pairs = summarize(event);
  const stamp = fmtDate(event.createdAt);
  const hasDetails = pairs.length > TOP_KV || !!event.diff.length || !!event.cause || !!event.effects.length;

  return (
    <article className={`ce-card ${actionClass(event.type)}${event.isBaseline ? " baseline" : ""}`} id={`ev-${event.id}`}>
      <span className="ce-card-icon">
        <Icon iconName={actionIcon(event.type)} size={20} />
      </span>

      <div className="ce-card-body">
        <span className="ce-action">{actionLabel(event.type)}</span>
        <Typography variant="paragraph-m" bold className="ce-type">
          <Highlight text={event.type} query={query} />
        </Typography>
        <Typography variant="paragraph-s" color="grey-600" className="ce-desc">
          {describeEvent(event)}
        </Typography>

        <div className="ce-facts">
          {pairs.slice(0, TOP_KV).map(([label, value]) => (
            <Fact label={label} value={value} query={query} key={`${label}-${value}`} />
          ))}
          <ChangeEventRawJson event={event} />
        </div>

        {open && (
          <div className="ce-details">
            <Actor event={event} query={query} />
            {pairs.length > TOP_KV && (
              <div className="ce-facts">
                {pairs.slice(TOP_KV).map(([label, value]) => (
                  <Fact label={label} value={value} query={query} key={`${label}-${value}`} />
                ))}
              </div>
            )}
            <ChangeEventDiffTable diff={event.diff} truncated={event.diffTruncated} query={query} />
            <ChangeEventCauseEffect event={event} query={query} onJump={onJump} />
          </div>
        )}
      </div>

      <aside className="ce-meta">
        <time className="ce-when" dateTime={event.createdAt} title={stamp.title}>
          <span className="ce-when-date">
            <Icon iconName="tabler/calendar" size={14} />
            {stamp.text.slice(0, 10)}
          </span>
          <span className="ce-when-time">
            <Icon iconName="tabler/clock" size={14} />
            {fmtTime(event.createdAt)}
          </span>
        </time>
        <div className="ce-tags">
          <span className={`ce-scope ${event.scope}`}>{event.scope}</span>
          {!!event.reportingYear && <span className="ce-year">{event.reportingYear}</span>}
        </div>
        <Amounts event={event} />
      </aside>

      {hasDetails && (
        <button type="button" className="ce-expand" aria-expanded={open} aria-label={open ? "hide details" : "show details"} onClick={() => setOpen(!open)}>
          <Icon iconName="tabler/chevron-right" size={16} />
        </button>
      )}
    </article>
  );
};
